import type {
  AnalysisExtraInsights,
  AnalysisResult,
  FollowersData,
  FollowingData,
  InstagramEntry,
  InstagramUser
} from '@/types/instagram';

const sanitizeUsername = (raw?: string | null): string | null => {
  if (typeof raw !== 'string') {
    return null;
  }

  let username = raw.trim();
  if (!username) {
    return null;
  }

  username = username.replace(/^@+/, '');
  username = username.replace(/\/+$/, '');
  username = username.replace(/^_u\//i, '');
  username = username.replace(/^www\./i, '');

  return username || null;
};

const extractFromTitle = (raw?: string | null): string | null => {
  if (typeof raw !== 'string') {
    return null;
  }

  const trimmed = raw.trim();
  if (!trimmed) {
    return null;
  }

  const handleMatches = trimmed.match(/@([A-Za-z0-9._]+)/g);
  if (handleMatches && handleMatches.length > 0) {
    for (const match of handleMatches) {
      const sanitized = sanitizeUsername(match);
      if (sanitized) {
        return sanitized;
      }
    }
  }

  const withoutParentheses = trimmed.replace(/\(.*?\)/g, ' ');
  const tokens = withoutParentheses.split(/\s+/);
  for (const token of tokens) {
    const sanitized = sanitizeUsername(token);
    if (sanitized) {
      return sanitized;
    }
  }

  return null;
};

const parseUsernameFromHref = (href?: string | null): string | null => {
  if (typeof href !== 'string') {
    return null;
  }

  const trimmedHref = href.trim();
  if (!trimmedHref) {
    return null;
  }

  let urlString = trimmedHref;
  if (!/^https?:\/\//i.test(urlString)) {
    urlString = `https://${urlString}`;
  }

  try {
    const url = new URL(urlString);
    const path = url.pathname.replace(/^\/+/, '').replace(/\/+$/, '');
    return sanitizeUsername(path);
  } catch {
    const fallback = trimmedHref
      .replace(/^https?:\/\/(www\.)?instagram\.com\//i, '')
      .replace(/\/+$/, '');
    return sanitizeUsername(fallback);
  }
};

const extractUsername = (entry: InstagramUser): string | null => {
  const cleanedValue = sanitizeUsername(entry.value);
  const valueLooksValid = cleanedValue ? !/\s/.test(cleanedValue) : false;

  if (valueLooksValid && cleanedValue) {
    return cleanedValue;
  }

  const fromTitle = extractFromTitle(entry.title);
  if (fromTitle) {
    return fromTitle;
  }

  const fromHref = parseUsernameFromHref(entry.href);
  if (fromHref) {
    return fromHref;
  }

  return cleanedValue;
};

export const normalizeForCompare = (username: string): string | null => {
  const sanitized = sanitizeUsername(username);
  return sanitized ? sanitized.toLowerCase() : null;
};

export interface NormalizedInstagramUser {
  username: string;
  normalized: string;
  timestamp?: number;
}

export const normalizeInstagramUserEntry = (
  entry: InstagramUser
): NormalizedInstagramUser | null => {
  const username = extractUsername(entry);
  if (!username) {
    return null;
  }

  const normalized = normalizeForCompare(username);
  if (!normalized) {
    return null;
  }

  return {
    username,
    normalized,
    timestamp: entry.timestamp,
  };
};

export const collectNormalizedFollowers = (
  followersData: FollowersData | { relationships_followers: FollowersData }
): NormalizedInstagramUser[] => {
  const followerEntries = Array.isArray(followersData)
    ? followersData
    : ('relationships_followers' in followersData
        ? followersData.relationships_followers
        : []);

  const followers: NormalizedInstagramUser[] = [];
  for (const entry of followerEntries) {
    if (entry.string_list_data) {
      for (const follower of entry.string_list_data) {
        const normalized = normalizeInstagramUserEntry(follower);
        if (normalized) {
          followers.push(normalized);
        }
      }
    }
  }
  return followers;
};

export const collectNormalizedFollowing = (
  followingData: FollowingData
): NormalizedInstagramUser[] => {
  const followingRelationships = Array.isArray(followingData)
    ? followingData
    : ('relationships_following' in followingData
        ? followingData.relationships_following
        : []);

  const following: NormalizedInstagramUser[] = [];
  for (const entry of followingRelationships) {
    if (entry.string_list_data) {
      for (const follow of entry.string_list_data) {
        const normalized = normalizeInstagramUserEntry(follow);
        if (normalized) {
          following.push(normalized);
        }
      }
    }
  }

  return following;
};

const extractEntriesArray = (data: unknown): InstagramEntry[] => {
  if (Array.isArray(data)) {
    return data as InstagramEntry[];
  }

  if (data && typeof data === 'object') {
    const record = data as Record<string, unknown>;
    for (const value of Object.values(record)) {
      if (Array.isArray(value)) {
        const first = value[0] as InstagramEntry | undefined;
        if (first && typeof first === 'object' && 'string_list_data' in first) {
          return value as InstagramEntry[];
        }
      }
    }
  }

  return [];
};

export const extractUsernamesFromData = (data: unknown): string[] => {
  const entries = extractEntriesArray(data);
  if (!entries.length) {
    return [];
  }

  const uniqueUsers = new Map<string, string>();
  for (const entry of entries) {
    if (!entry.string_list_data) continue;
    for (const user of entry.string_list_data) {
      const normalized = normalizeInstagramUserEntry(user);
      if (normalized) {
        uniqueUsers.set(normalized.normalized, normalized.username);
      }
    }
  }

  return Array.from(uniqueUsers.values());
};

export const computeInstagramAnalysis = (
  followersData: FollowersData | { relationships_followers: FollowersData },
  followingData: FollowingData,
  extraInsights?: AnalysisExtraInsights
): AnalysisResult => {
  const followerUsers = collectNormalizedFollowers(followersData);
  const followingUsers = collectNormalizedFollowing(followingData);

  const followers = followerUsers.map(user => user.username);
  const followerSet = new Set(followerUsers.map(user => user.normalized));

  const following = followingUsers.map(user => user.username);
  const followingSet = new Set(followingUsers.map(user => user.normalized));

  const notFollowingYouBack = followingUsers
    .filter(user => !followerSet.has(user.normalized))
    .map(user => user.username);
  const notFollowingBack = followerUsers
    .filter(user => !followingSet.has(user.normalized))
    .map(user => user.username);

  return {
    followers: followers.length,
    following: following.length,
    notFollowingYouBack,
    notFollowingBack,
    followersList: followers,
    followingList: following,
    processedAt: new Date().toISOString(),
    ...(extraInsights ? { extraInsights } : {})
  };
};
